import React, { useState, useEffect } from 'react';
import { TeamMember } from '../../types';
import { teamApi } from '../../services/api';
import Modal from '../../components/Modal';
import { EyeIcon, PencilIcon, TrashIcon } from '../../components/icons';

const ManageTeamPage: React.FC = () => {
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState<Partial<TeamMember>>({});
  const [saving, setSaving] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState<string>('');
  const [profilePreview, setProfilePreview] = useState<string>('');

  useEffect(() => {
    fetchTeam();
  }, []);

  const fetchTeam = async () => {
    try {
      setLoading(true);
      const response = await teamApi.getAll();
      setTeam(response.data.data.members);
      setError(null);
    } catch (err: any) {
      setError('Failed to load team members.');
    } finally {
      setLoading(false);
    }
  };

  const openViewModal = (member: TeamMember) => {
    setSelectedMember(member);
    setIsEditing(false);
    setIsModalOpen(true);
  };

  const openEditModal = (member: TeamMember) => {
    setSelectedMember(member);
    setForm(member);
    setAvatarPreview(member.avatarImage || '');
    setProfilePreview(member.profileImage || '');
    setIsEditing(true);
    setIsModalOpen(true);
  };

  const openAddModal = () => {
    setSelectedMember(null);
    setForm({ name: '', role: '', bio: '', specialties: [] });
    setAvatarPreview('');
    setProfilePreview('');
    setIsEditing(true);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedMember(null);
    setIsEditing(false);
    setForm({});
    setAvatarPreview('');
    setProfilePreview('');
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleSpecialtiesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, specialties: e.target.value.split(',').map(s => s.trim()) });
  };

  const compressImage = (file: File, maxWidth: number, maxHeight: number): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Calculate new dimensions
          if (width > height) {
            if (width > maxWidth) {
              height = (height * maxWidth) / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = (width * maxHeight) / height;
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0, width, height);

          // Compress to JPEG with 0.85 quality
          const compressedBase64 = canvas.toDataURL('image/jpeg', 0.85);
          resolve(compressedBase64);
        };
        img.onerror = reject;
      };
      reader.onerror = reject;
    });
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, type: 'avatar' | 'profile') => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file type
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }

    try {
      // Compress image based on type
      const maxSize = type === 'avatar' ? 400 : 800;
      const compressedBase64 = await compressImage(file, maxSize, maxSize);

      // Check compressed size
      const sizeInMB = (compressedBase64.length * 3) / 4 / (1024 * 1024);
      if (sizeInMB > 2) {
        alert(`Image is still too large after compression (${sizeInMB.toFixed(2)}MB). Please use a smaller image.`);
        return;
      }

      if (type === 'avatar') {
        setForm({ ...form, avatarImage: compressedBase64 });
        setAvatarPreview(compressedBase64);
      } else {
        setForm({ ...form, profileImage: compressedBase64 });
        setProfilePreview(compressedBase64);
      }
    } catch (error) {
      alert('Failed to process image. Please try again.');
      console.error('Image processing error:', error);
    }
  };

  const removeImage = (type: 'avatar' | 'profile') => {
    if (type === 'avatar') {
      setForm({ ...form, avatarImage: undefined });
      setAvatarPreview('');
    } else {
      setForm({ ...form, profileImage: undefined });
      setProfilePreview('');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (selectedMember) {
        await teamApi.update(selectedMember._id, form);
      } else {
        await teamApi.create(form);
      }
      await fetchTeam();
      closeModal();
    } catch (err) {
      alert('Failed to save team member.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this team member?')) return;
    try {
      await teamApi.delete(id);
      await fetchTeam();
    } catch (err) {
      alert('Failed to delete team member.');
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold text-white">Our Team</h2>
      <p className="text-neutral-400">Manage the team members shown in the "Meet Our Team" section on the public site.</p>
      <div className="flex justify-end mb-4">
        <button onClick={openAddModal} className="bg-primary hover:bg-primary-dark text-white font-medium py-2 px-4 rounded-lg transition-colors">+ Add Team Member</button>
      </div>
      {loading ? (
        <div className="flex justify-center items-center min-h-[100px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      ) : error ? (
        <div className="text-center py-8">
          <p className="text-red-500 mb-4">{error}</p>
        </div>
      ) : (
        <div className="bg-neutral-dark shadow-xl rounded-lg overflow-x-auto">
          <table className="w-full text-sm text-left text-neutral-300">
            <thead className="text-xs text-neutral-100 uppercase bg-neutral-darker">
              <tr>
                <th className="px-6 py-3">Name</th>
                <th className="px-6 py-3">Role</th>
                <th className="px-6 py-3">Bio</th>
                <th className="px-6 py-3">Specialties</th>
                <th className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {team.map((member) => (
                <tr key={member._id} className="bg-neutral-dark border-b border-neutral-700 hover:bg-neutral-dark/70">
                  <td className="px-6 py-4 font-medium text-white whitespace-nowrap">{member.name}</td>
                  <td className="px-6 py-4">{member.role}</td>
                  <td className="px-6 py-4">{member.bio}</td>
                  <td className="px-6 py-4">{member.specialties?.join(', ')}</td>
                  <td className="px-6 py-4 text-right space-x-2">
                    <button onClick={() => openViewModal(member)} className="text-sky-400 hover:text-sky-300 p-1" title="View Details">
                      <EyeIcon className="w-5 h-5" />
                    </button>
                    <button onClick={() => openEditModal(member)} className="text-primary-light hover:text-primary p-1" title="Edit">
                      <PencilIcon className="w-5 h-5" />
                    </button>
                    <button onClick={() => handleDelete(member._id)} className="text-red-500 hover:text-red-400 p-1" title="Delete">
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {isModalOpen && (
        <Modal isOpen={isModalOpen} onClose={closeModal} title={isEditing ? (selectedMember ? 'Edit Team Member' : 'Add Team Member') : 'Team Member Details'}>
          {isEditing ? (
            <div className="space-y-4 text-neutral-light">
              <div>
                <label className="block text-sm font-medium mb-1">Name</label>
                <input type="text" name="name" value={form.name || ''} onChange={handleFormChange} className="w-full bg-neutral-darker p-2 rounded border border-neutral-600 focus:ring-primary focus:border-primary" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Role</label>
                <input type="text" name="role" value={form.role || ''} onChange={handleFormChange} className="w-full bg-neutral-darker p-2 rounded border border-neutral-600 focus:ring-primary focus:border-primary" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Bio</label>
                <textarea name="bio" value={form.bio || ''} onChange={handleFormChange} rows={3} className="w-full bg-neutral-darker p-2 rounded border border-neutral-600 focus:ring-primary focus:border-primary" />
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Avatar Image (400x400 recommended)</label>
                  <input 
                    type="file" 
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(e) => handleImageUpload(e, 'avatar')}
                    className="w-full bg-neutral-darker p-2 rounded border border-neutral-600 focus:ring-primary focus:border-primary"
                  />
                  {avatarPreview && (
                    <div className="mt-2 relative">
                      <img src={avatarPreview} alt="Avatar preview" className="w-24 h-24 rounded-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removeImage('avatar')}
                        className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1 text-xs"
                      >
                        ✕
                      </button>
                    </div>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Profile Image (800x800 recommended)</label>
                  <input 
                    type="file" 
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(e) => handleImageUpload(e, 'profile')}
                    className="w-full bg-neutral-darker p-2 rounded border border-neutral-600 focus:ring-primary focus:border-primary"
                  />
                  {profilePreview && (
                    <div className="mt-2 relative">
                      <img src={profilePreview} alt="Profile preview" className="w-32 h-32 rounded object-cover" />
                      <button
                        type="button"
                        onClick={() => removeImage('profile')}
                        className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1 text-xs"
                      >
                        ✕
                      </button>
                    </div>
                  )}
                </div>
                
                {form.imageUrl && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Legacy Image URL (optional)</label>
                    <input type="text" name="imageUrl" value={form.imageUrl || ''} onChange={handleFormChange} className="w-full bg-neutral-darker p-2 rounded border border-neutral-600 focus:ring-primary focus:border-primary" />
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Specialties (comma separated)</label>
                <input type="text" name="specialties" value={form.specialties?.join(', ') || ''} onChange={handleSpecialtiesChange} className="w-full bg-neutral-darker p-2 rounded border border-neutral-600 focus:ring-primary focus:border-primary" />
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button type="button" onClick={closeModal} className="px-4 py-2 rounded bg-neutral-600 hover:bg-neutral-500 transition-colors">Cancel</button>
                <button type="button" onClick={handleSave} className="px-4 py-2 rounded bg-primary hover:bg-primary-dark text-white transition-colors" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
              </div>
            </div>
          ) : selectedMember && (
            <div className="text-neutral-light space-y-3">
              <p><strong>Name:</strong> {selectedMember.name}</p>
              <p><strong>Role:</strong> {selectedMember.role}</p>
              <p><strong>Bio:</strong> {selectedMember.bio}</p>
              <p><strong>Specialties:</strong> {selectedMember.specialties?.join(', ')}</p>
              {(selectedMember.avatarImage || selectedMember.profileImage) && (
                <div className="flex gap-4">
                  {selectedMember.avatarImage && (
                    <div>
                      <p className="font-semibold mb-1">Avatar:</p>
                      <img src={selectedMember.avatarImage} alt="Avatar" className="w-24 h-24 rounded-full object-cover" />
                    </div>
                  )}
                  {selectedMember.profileImage && (
                    <div>
                      <p className="font-semibold mb-1">Profile:</p>
                      <img src={selectedMember.profileImage} alt="Profile" className="w-32 h-32 rounded object-cover" />
                    </div>
                  )}
                </div>
              )}
              {selectedMember.imageUrl && <p><strong>Legacy Image URL:</strong> {selectedMember.imageUrl}</p>}
              <div className="text-right pt-4">
                <button type="button" onClick={closeModal} className="px-4 py-2 rounded bg-primary hover:bg-primary-dark text-white transition-colors">Close</button>
              </div>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
};

export default ManageTeamPage; 